import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import Compressor from "compressorjs";
import {
  addMonths,
  addWeeks,
  differenceInCalendarDays,
  format,
  isSameMonth,
  isSameWeek,
  isSameYear,
  parseISO,
  subMonths,
  subWeeks,
} from "date-fns";
import { useCallback, useMemo } from "react";
import { JobTypes } from "@vellum/vellum-api";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const usdFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export function formatCurrency(amount: number): string {
  return usdFormatter.format(amount);
}

/** Rendered in place of a value the source has no data for. */
export const EMPTY_PLACEHOLDER = "\u2014";

/**
 * Coerces a numeric field to a number, tolerating the string form a
 * JSON-serialized decimal arrives as. Anything missing or unparseable is null.
 */
function toFiniteNumber(
  value: number | string | null | undefined,
): number | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Formats a rating out of a maximum as "4 / 5". */
export function formatRating(
  value: number | string | null | undefined,
  outOf: number = 5,
): string {
  const rating = toFiniteNumber(value);
  return rating === null ? EMPTY_PLACEHOLDER : `${rating} / ${outOf}`;
}

/** Formats a number to a fixed number of decimal places. */
export function formatDecimal(
  value: number | string | null | undefined,
  fractionDigits: number,
): string {
  const parsed = toFiniteNumber(value);
  return parsed === null ? EMPTY_PLACEHOLDER : parsed.toFixed(fractionDigits);
}

/**
 * Formats a percentage magnitude with a trailing "%" — a value of `53.7` renders
 * as "53.7%". Values already expressed as a 0-1 ratio must be scaled by the
 * caller.
 */
export function formatPercent(
  value: number | string | null | undefined,
  fractionDigits: number = 1,
): string {
  const parsed = toFiniteNumber(value);
  return parsed === null
    ? EMPTY_PLACEHOLDER
    : `${parsed.toFixed(fractionDigits)}%`;
}

export function getFriendlyDate(date: string): string {
  const dt = new Date(date);
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  };

  return dt.toLocaleDateString(undefined, options);
}

/**
 * Formats a plain `YYYY-MM-DD` date string as "March 2026". The date is built
 * from its parts rather than parsed, so a date-only string can't be read as UTC
 * midnight and slip into the previous month in western timezones. Returns the
 * input unchanged if it isn't a parseable date string.
 */
export function getFriendlyMonthYear(date: string): string {
  const [year, month] = date.split("-").map(Number);
  if (!year || !month) {
    return date;
  }

  return new Date(year, month - 1, 1).toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });
}

export function getFriendlyDateTime(date: string): string {
  const dt = new Date(date);
  const now = new Date();
  if (dt.getFullYear() === now.getFullYear()) {
    const options: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    };
    return dt.toLocaleDateString(undefined, options);
  } else {
    const options: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
    };
    return dt.toLocaleDateString(undefined, options);
  }
}

export function getFriendlyDOB(date: string): string {
  const dt = new Date(date);
  const options: Intl.DateTimeFormatOptions = {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  };
  return dt.toLocaleDateString(undefined, options);
}

/** Same local calendar day as `now`: minute/hour-style relative label (past and future). */
function formatIntraDayRelativeLabel(date: Date, now: Date): string {
  const diffMs = now.getTime() - date.getTime();
  if (diffMs >= 0) {
    const diffInHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffInHours === 0) {
      const diffInMinutes = Math.floor(diffMs / (1000 * 60));
      if (diffInMinutes === 0) return "Just now";
      return diffInMinutes === 1
        ? "1 minute ago"
        : `${diffInMinutes} minutes ago`;
    }
    return diffInHours === 1 ? "1 hour ago" : `${diffInHours} hours ago`;
  }
  const aheadMs = -diffMs;
  const diffInHours = Math.floor(aheadMs / (1000 * 60 * 60));
  if (diffInHours === 0) {
    const diffInMinutes = Math.floor(aheadMs / (1000 * 60));
    if (diffInMinutes === 0) return "Soon";
    return diffInMinutes === 1 ? "in 1 minute" : `in ${diffInMinutes} minutes`;
  }
  return diffInHours === 1 ? "in 1 hour" : `in ${diffInHours} hours`;
}

/** ISO date or datetime string; calendar labels relative to today (past and future). */
interface HumanRelativeCalendarLabelProps {
  /** Same local day: use minute/hour relative text (e.g. "5 minutes ago") instead of "Today". */
  detailed?: boolean;
}
export function humanRelativeCalendarLabel(
  iso: string,
  opts?: HumanRelativeCalendarLabelProps,
): string {
  const { detailed = false } = opts || {};
  const date = parseISO(iso);
  const now = new Date();
  const diff = differenceInCalendarDays(now, date); // >0 past, <0 future

  if (diff === 0) {
    if (detailed) return formatIntraDayRelativeLabel(date, now);
    return "Today";
  }

  if (diff > 0) {
    if (diff === 1) return "Yesterday";
    if (isSameWeek(date, now)) return "This week";
    if (isSameWeek(date, subWeeks(now, 1))) return "Last week";
    if (isSameMonth(date, now) && isSameYear(date, now)) return "This month";
    const lastMonth = subMonths(now, 1);
    if (isSameMonth(date, lastMonth) && isSameYear(date, lastMonth)) {
      return "Last month";
    }
    if (isSameYear(date, now)) return format(date, "MMM d");
    return format(date, "MMM d, yyyy");
  }

  const until = -diff;
  if (until === 1) return "Tomorrow";
  if (isSameWeek(date, now)) return "This week";
  if (isSameWeek(date, addWeeks(now, 1))) return "Next week";
  if (isSameMonth(date, now) && isSameYear(date, now)) return "This month";
  const nextMonth = addMonths(now, 1);
  if (isSameMonth(date, nextMonth) && isSameYear(date, nextMonth)) {
    return "Next month";
  }
  if (isSameYear(date, now)) return format(date, "MMM d");
  return format(date, "MMM d, yyyy");
}

export function getRelativeDate(date: string): string {
  const inputDate = new Date(date);
  const now = new Date();
  const diffInMs = now.getTime() - inputDate.getTime();
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

  // If more than 7 days ago, use friendly date format
  if (diffInDays > 7) {
    return getFriendlyDate(inputDate.toISOString());
  }

  // Handle relative dates for recent dates
  if (diffInDays === 0) {
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    if (diffInHours === 0) {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      if (diffInMinutes === 0) {
        return "Just now";
      }
      return diffInMinutes === 1
        ? "1 minute ago"
        : `${diffInMinutes} minutes ago`;
    }
    return diffInHours === 1 ? "1 hour ago" : `${diffInHours} hours ago`;
  }

  if (diffInDays === 1) {
    return "Yesterday";
  }

  return `${diffInDays}d ago`;
}

export function compressFile(file: File): Promise<File> {
  return new Promise((resolve) => {
    // Only compress image files, leave PDFs and other file types as-is
    if (!file.type.startsWith("image/")) {
      resolve(file);
      return;
    }

    new Compressor(file, {
      retainExif: true,
      success: (compressedFile) => {
        const compressedWithName = new File([compressedFile], file.name, {
          type: compressedFile.type,
          lastModified: Date.now(),
        });
        resolve(compressedWithName);
      },
      error: (error) => {
        console.warn("Compression failed, using original file:", error);
        resolve(file);
      },
    });
  });
}

export function getShortId(id: string): string {
  return id.split("-")[0];
}

export const debounce = (func: Function, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

export const useDebouncedSearch = (
  setSearchQuery: (query: string) => void,
  delay: number = 200,
) => {
  const debouncedFunction = useCallback((func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }, []);

  const debouncedSearch = useMemo(
    () => debouncedFunction((query: string) => setSearchQuery(query), delay),
    [debouncedFunction, setSearchQuery, delay],
  );

  return debouncedSearch;
};

export const LunaUrlMap: Record<string, string> = {
  local: "http://luna.local.vibestack.cloud",
  dev: "https://app.dev.vellum.health",
  prod: "https://app.vellum.health",
};

/** Each job type's path segment under the app's `/jobs`. */
const JOB_TYPE_SEGMENTS: Record<JobTypes, string> = {
  [JobTypes.INFUSION]: "infusions",
  [JobTypes.VASCULAR_ACCESS]: "vascular-access",
  [JobTypes.TRANSFUSION]: "transfusions",
};

/** The app's path to a job type's pages, e.g. `/jobs/infusions`. */
export function jobTypePath(jobType: JobTypes): string {
  return `/jobs/${JOB_TYPE_SEGMENTS[jobType]}`;
}

/** The app's path to a job's details page. */
export function jobDetailPath(jobType: JobTypes, id: string): string {
  return `${jobTypePath(jobType)}/${id}`;
}

/** The app's job details page as a full URL, for links from other apps. */
export function appJobUrl(env: string, jobType: JobTypes, id: string): string {
  return `${LunaUrlMap[env]}${jobDetailPath(jobType, id)}`;
}

export function toTitleCase(str: string): string {
  return str.replace(
    /\w\S*/g,
    (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
  );
}

/**
 * URL-safe slug: lowercase alphanumerics joined by single hyphens, with no
 * leading or trailing hyphen. Matches the slug rule the Vellum API enforces.
 */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
